import { Avatar, CollapseProps, Divider, Tree, Typography } from 'antd';
import {
    TbBuilding, TbChevronDown, TbCurrentLocation, TbInfoCircle, TbObjectScan, TbProgressHelp, TbRosetteDiscountCheckFilled, TbUserCode, } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { SmallTooltip } from '@/shared';
import { buildOAuthScopeTree, getOAuthScopeDefinitions, OAuthConsentRequest } from '@/shared/oauth/scopes';
import { corporateLogo } from '@/utilities';

export const useOAuthConsentCollapsedItems = (request?: OAuthConsentRequest) => {
    const { token } = useAntDesignTheme();

    const scopes: string[] = request?.requestedScopes ?? [];

    const definitions = getOAuthScopeDefinitions(scopes);
    const treeOptions = {
        colorBorder: token.colorBorder,
        colorBgContainer: token.colorBgContainer,
        colorTextTertiary: token.colorTextTertiary,
        includeDescriptions: false,
    };
    const accountTreeData = buildOAuthScopeTree(
        definitions.filter(({ category }) => category === 'account'),
        treeOptions,
    );
    const workspaceTreeData = buildOAuthScopeTree(
        definitions.filter(({ category }) => category !== 'account'),
        treeOptions,
    );

    const hasAccountScopes = accountTreeData.length > 0;
    const hasWorkspaceScopes = workspaceTreeData.length > 0;

    const items: CollapseProps['items'] = [
        {
            key: 'applicationOverview',
            label: (
                <>
                    <TbObjectScan size='1rem' style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }} />
                    Application Overview
                </>
            ),
            children: (
                <div
                    style={{
                        maxHeight: 16 * 18,
                        overflow: 'scroll',
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadius * 2,
                        padding: '0.5rem 0.875rem',
                    }}
                >
                    <div style={{ marginBottom: '0.25rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>Description</small>
                    </div>
                    <div>{request?.client?.shortDescription ?? '-'}</div>
                    <Divider style={{ margin: '0.75rem 0' }} />
                    <div style={{ marginBottom: '0.25rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>Overview</small>
                    </div>
                    <div>{request?.client?.overview ?? '-'}</div>
                    <Divider style={{ margin: '0.75rem 0' }} />
                    <div style={{ marginBottom: '0.25rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>Integration Type</small>
                    </div>
                    <div>{request?.client?.isPublic ? 'Public' : 'Private'}</div>
                    {request?.client?.isPublic && (
                        <>
                            <Divider style={{ margin: '0.75rem 0' }} />
                            <div style={{ marginBottom: '0.25rem' }}>
                                <small style={{ color: token.colorTextTertiary }}>
                                    Public Integration Directory URL
                                </small>
                            </div>
                            <div>
                                {request?.client?.slug && (
                                    <a href={`https://countercyclical.com/integrations/${request?.client?.slug}`}>
                                        https://countercyclical.com/integrations/{request?.client?.slug}
                                    </a>
                                )}
                            </div>
                        </>
                    )}
                </div>
            ),
            extra: (
                <SmallTooltip title='An overview of the integration.' placement='right' padding='0 1.5rem'>
                    <TbInfoCircle style={{ marginTop: '0.25rem', color: token.colorTextTertiary }} />
                </SmallTooltip>
            ),
        },
        {
            key: 'scopes',
            label: (
                <>
                    <TbCurrentLocation size='1rem' style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }} />
                    Integration Scope
                </>
            ),
            children: (
                <div
                    style={{
                        maxHeight: 16 * 18,
                        overflow: 'scroll',
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadius * 2,
                        padding: '0.5rem',
                    }}
                >
                    {hasAccountScopes && (
                        <>
                            <div style={{ margin: '0 0.25rem', marginBottom: '0.5rem' }}>
                                <Typography.Title level={5}>Account Scopes</Typography.Title>
                                <Typography.Text type='secondary'>Scopes relating to your account.</Typography.Text>
                            </div>
                            <Tree
                                switcherIcon={(props) => (
                                    <TbChevronDown
                                        style={{
                                            transform: `rotate(${props.expanded ? '0deg' : '-90deg'})`,
                                            color: token.colorTextTertiary,
                                        }}
                                    />
                                )}
                                showLine
                                selectable={false}
                                checkable={false}
                                defaultExpandAll
                                treeData={accountTreeData}
                                blockNode
                            />
                            <Divider style={{ margin: '1rem 0' }} />
                        </>
                    )}
                    {hasWorkspaceScopes && (
                        <>
                            <div style={{ margin: '0 0.25rem', marginBottom: '0.5rem' }}>
                                <Typography.Title level={5}>Workspace & Resource Scopes</Typography.Title>
                                <Typography.Text type='secondary'>
                                    Scopes relating to your workspace data.
                                </Typography.Text>
                            </div>
                            <Tree
                                switcherIcon={(props) => (
                                    <TbChevronDown
                                        style={{
                                            transform: `rotate(${props.expanded ? '0deg' : '-90deg'})`,
                                            color: token.colorTextTertiary,
                                        }}
                                    />
                                )}
                                showLine
                                selectable={false}
                                checkable={false}
                                defaultExpandAll
                                treeData={workspaceTreeData}
                                blockNode
                            />
                        </>
                    )}
                </div>
            ),
            extra: (
                <SmallTooltip
                    title='The scope of the resources you will be giving access to in your Workspace.'
                    placement='right'
                    padding='0 1.5rem'
                >
                    <TbInfoCircle style={{ marginTop: '0.25rem', color: token.colorTextTertiary }} />
                </SmallTooltip>
            ),
        },
        {
            key: 'developer',
            label: (
                <>
                    <TbUserCode size='1rem' style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }} />
                    Developer Information
                </>
            ),
            children: (
                <div
                    style={{
                        maxHeight: 16 * 18,
                        overflow: 'scroll',
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadius * 2,
                        padding: '0.5rem 0.75rem',
                    }}
                >
                    <section>
                        <div style={{ marginBottom: '0.125rem' }}>
                            <small style={{ color: token.colorTextTertiary }}>Developer Name</small>
                        </div>
                        <div>
                            <Avatar
                                size={16}
                                shape='square'
                                style={{
                                    border: `1px solid ${token.colorBorder}`,
                                    backgroundColor: token.colorBgContainer,
                                    marginRight: '0.325rem',
                                    verticalAlign: request?.client?.websiteUrl ? '-0.125rem' : 0,
                                }}
                                icon={<TbBuilding size='75%' style={{ color: token.colorTextTertiary }} />}
                                src={request?.client?.websiteUrl && corporateLogo(request?.client?.websiteUrl)}
                            />
                            {request?.client?.developerName ?? 'Untitled Developer'}
                            {request?.client?.developerVerified ? (
                                <SmallTooltip
                                    title={<>This developer has been officially verified by Countercyclical.</>}
                                    placement='right'
                                    padding='0 0.25rem'
                                >
                                    <TbRosetteDiscountCheckFilled
                                        size='1rem'
                                        style={{
                                            color: token.cyan6,
                                            verticalAlign: '-0.2rem',
                                            marginLeft: '0.375rem',
                                        }}
                                    />
                                </SmallTooltip>
                            ) : (
                                <SmallTooltip
                                    title={<>This developer has not been officially verified by Countercyclical.</>}
                                    placement='right'
                                    padding='0 0.25rem'
                                >
                                    <TbProgressHelp
                                        size='1rem'
                                        style={{
                                            color: token.orange6,
                                            verticalAlign: '-0.2rem',
                                            marginLeft: '0.375rem',
                                        }}
                                    />
                                </SmallTooltip>
                            )}
                        </div>
                        <Divider style={{ margin: '1rem 0' }} />
                        {/* !TODO: Look into adding 'copy to clipboard' buttons next to links */}
                        {request?.client?.supportEmail && (
                            <>
                                <div style={{ marginBottom: '0.125rem' }}>
                                    <small style={{ color: token.colorTextTertiary }}>Support Email</small>
                                </div>
                                <div style={{ marginBottom: '0.125rem' }}>
                                    <a href={`mailto:${request?.client?.supportEmail}`}>
                                        {request?.client?.supportEmail}
                                    </a>
                                </div>
                            </>
                        )}
                        {(request?.client?.supportURL || request?.client?.supportUrl) && (
                            <>
                                <div style={{ marginBottom: '0.125rem' }}>
                                    <small style={{ color: token.colorTextTertiary }}>Support URL</small>
                                </div>

                                <div style={{ marginBottom: '0.125rem' }}>
                                    <a href={request?.client?.supportURL || request?.client?.supportUrl}>
                                        {request?.client?.supportURL || request?.client?.supportUrl}
                                    </a>
                                </div>
                            </>
                        )}
                        <div style={{ marginBottom: '0.125rem' }}>
                            <small style={{ color: token.colorTextTertiary }}>Website URL</small>
                        </div>
                        <div style={{ marginBottom: '0.125rem' }}>
                            <a href={request?.client?.websiteUrl}>{request?.client?.websiteUrl}</a>
                        </div>
                        {request?.client?.installationUrl && (
                            <>
                                <div style={{ marginBottom: '0.125rem' }}>
                                    <small style={{ color: token.colorTextTertiary }}>Installation URL</small>
                                </div>
                                <div style={{ marginBottom: '0.125rem' }}>
                                    <a href={request?.client?.installationUrl}>{request?.client?.installationUrl}</a>
                                </div>
                            </>
                        )}
                    </section>
                </div>
            ),
            extra: (
                <SmallTooltip
                    title='Information about the developer of this integration.'
                    placement='right'
                    padding='0 1.5rem'
                >
                    <TbInfoCircle style={{ marginTop: '0.25rem', color: token.colorTextTertiary }} />
                </SmallTooltip>
            ),
        },
    ];

    return { items, accountTreeData, workspaceTreeData };
};
